import React, { useEffect, useRef, useState } from 'react';
import { learningApi } from '../../services/api';

/**
 * A YouTube video for learning, with the rules the course needs:
 *
 *  - No skipping ahead: rewinding is fine, but the video cannot jump past the
 *    furthest point already watched — a jump forward is sent back there.
 *  - Speed stays at 1×.
 *  - Learning time counts only while the video is actually playing and the
 *    page is visible.
 *  - Every 10 s (and on pause, end and close) the portal is told; the server
 *    checks the figures and decides when the feature is learned.
 *  - Closing early keeps the feature in progress; it resumes where it stopped.
 */

declare global { interface Window { YT?: any; onYouTubeIframeAPIReady?: () => void } }

let ytReady: Promise<any> | null = null;
function loadYouTube(): Promise<any> {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!ytReady) {
    ytReady = new Promise(resolve => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(window.YT); };
      const s = document.createElement('script');
      s.src = 'https://www.youtube.com/iframe_api';
      s.async = true;
      document.head.appendChild(s);
    });
  }
  return ytReady;
}

export interface LearnerProgress { learned: boolean; watched_seconds: number; max_position: number; duration: number; short_by?: number }

const YouTubeLearner: React.FC<{
  featureId: number;
  youtubeId: string;
  onProgress?: (p: LearnerProgress) => void;
  onLearned?: () => void;
}> = ({ featureId, youtubeId, onProgress, onLearned }) => {
  const host = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let player: any = null, cancelled = false;
    let session = 0, maxAllowed = 0, played = 0, lastTick = 0, lastPos = 0, learnedOnce = false;
    let tick: any = null, beat: any = null, noticeTimer: any = null;
    const flash = (m: string) => { setNotice(m); clearTimeout(noticeTimer); noticeTimer = setTimeout(() => setNotice(null), 3500); };

    const state = () => (player?.getPlayerState ? player.getPlayerState() : -1);
    const PLAYING = 1, ENDED = 0;
    const now = () => performance.now();

    const send = async (extra: { ended?: boolean } = {}) => {
      if (!session || !player?.getCurrentTime) return;
      const body = { session_id: session, played: Math.round(played), position: Math.floor(player.getCurrentTime() || 0), duration: Math.floor(player.getDuration?.() || 0), ...extra };
      played = 0;
      try {
        const r = (await learningApi.heartbeat(body)).data;
        maxAllowed = Math.max(maxAllowed, Number(r.max_position) || 0);
        onProgress?.({ learned: !!r.learned, watched_seconds: r.watched_seconds, max_position: r.max_position, duration: r.duration, short_by: r.short_by });
        if (r.learned && !learnedOnce) { learnedOnce = true; onLearned?.(); }
        if (extra.ended && !r.learned && r.short_by > 0) flash(`Not counted as learned yet — about ${Math.ceil(r.short_by / 60)} min of the video still to be watched.`);
      } catch { played += body.played; /* try again on the next beat */ }
    };
    const closing = () => {
      if (!session || !player?.getCurrentTime) return;
      learningApi.heartbeatClosing({ session_id: session, played: Math.round(played), position: Math.floor(player.getCurrentTime() || 0), duration: Math.floor(player.getDuration?.() || 0) });
      played = 0;
    };

    (async () => {
      try {
        const start = (await learningApi.start(featureId)).data;
        if (cancelled) return;
        session = start.session_id;
        maxAllowed = Number(start.max_position) || 0;
        learnedOnce = start.status === 'learned';
        const YT = await loadYouTube();
        if (cancelled || !host.current) return;
        player = new YT.Player(host.current, {
          videoId: youtubeId,
          width: '100%', height: '100%',
          playerVars: { rel: 0, modestbranding: 1, disablekb: 1, playsinline: 1, start: Math.floor(start.resume_at || 0), iv_load_policy: 3 },
          events: {
            onReady: () => { lastPos = Math.floor(start.resume_at || 0); },
            onStateChange: (e: any) => {
              if (e.data === ENDED) send({ ended: true });
              else if (e.data === 2 /* paused */) send();
              lastTick = now();
            },
            // Speed stays at 1×.
            onPlaybackRateChange: (e: any) => { if (e.data !== 1) { player.setPlaybackRate(1); flash('Videos play at normal speed for learning.'); } },
            onError: () => setError('This video cannot be played here. It may be private, removed, or not allowed to be embedded.'),
          },
        });

        // Twice a second: count playing time, and stop jumps past the furthest point watched.
        tick = setInterval(() => {
          if (!player?.getCurrentTime) return;
          const t = player.getCurrentTime() || 0, n = now();
          if (state() === PLAYING) {
            if (t > maxAllowed + 2 && t > lastPos + 2) {
              player.seekTo(maxAllowed, true);
              flash('Skipping ahead is not allowed — watch the video through. You can go back any time.');
            } else {
              if (t <= maxAllowed + 2) maxAllowed = Math.max(maxAllowed, t);
              if (!document.hidden && lastTick) played += Math.min((n - lastTick) / 1000, 1.5);
            }
          }
          lastPos = t; lastTick = n;
        }, 500);
        beat = setInterval(() => { if (state() === PLAYING || played >= 1) send(); }, 10000);
      } catch (e: any) {
        setError(e?.message || 'Could not open this feature');
      }
    })();

    // Another tab or app in front: pause and save. The sitting carries on when they come back.
    const onHide = () => { if (document.hidden && state() === PLAYING) { player?.pauseVideo?.(); send(); } };
    // Closing the page or leaving the course: the last report, and the sitting ends.
    let closedOnce = false;
    const closingOnce = () => { if (!closedOnce) { closedOnce = true; closing(); } };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', closingOnce);
    return () => {
      cancelled = true;
      clearInterval(tick); clearInterval(beat); clearTimeout(noticeTimer);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', closingOnce);
      closingOnce();
      try { player?.destroy?.(); } catch { /* gone */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureId, youtubeId]);

  return (
    <div className="relative w-full bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '16 / 9' }}>
      {error ? (
        <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-red-200">{error}</div>
      ) : (
        <div ref={host} className="absolute inset-0" />
      )}
      {notice && (
        <div className="absolute left-1/2 -translate-x-1/2 top-3 px-3 py-1.5 rounded bg-black/80 text-white text-xs shadow">{notice}</div>
      )}
    </div>
  );
};

export default YouTubeLearner;
